import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  ArrowRight,
  BadgeCheck,
  Bell,
  Building2,
  CreditCard,
  FileText,
  LogOut,
  Save,
  Trash2,
  Wallet,
  X,
  Receipt,
  ShieldCheck,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { toast } from "sonner";

const BRAND_OPTIONS = ["Audi", "BMW", "Mercedes", "Volkswagen", "Porsche", "Tesla", "Fiat", "Alfa Romeo", "Volvo", "Toyota"];

export default function ProfilePage() {
  const { t, i18n } = useTranslation();
  const { user, logout, reload } = useAuth();
  const [dealer, setDealer] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);

  const [name, setName] = useState(user?.name || "");
  const [preferredBrands, setPreferredBrands] = useState(user?.preferred_brands || []);
  const [targetMargin, setTargetMargin] = useState(user?.target_margin ?? 3000);
  const [notifEmail, setNotifEmail] = useState(user?.notifications_email ?? true);
  const [notifPush, setNotifPush] = useState(user?.notifications_push ?? false);
  const [savingUser, setSavingUser] = useState(false);

  const [dealerName, setDealerName] = useState("");
  const [dealerAddress, setDealerAddress] = useState("");
  const [dealerPhone, setDealerPhone] = useState("");
  const [dealerEmail, setDealerEmail] = useState("");
  const [dealerDesc, setDealerDesc] = useState("");
  const [vatNumber, setVatNumber] = useState("");
  const [savingDealer, setSavingDealer] = useState(false);

  const [showAddPayment, setShowAddPayment] = useState(false);
  const [newPm, setNewPm] = useState({ kind: "card", label: "", last4: "", iban_tail: "", holder: "" });

  useEffect(() => {
    if (user?.dealer_id) {
      api.get(`/dealers/${user.dealer_id}`).then((r) => {
        setDealer(r.data);
        setDealerName(r.data.name || "");
        setDealerAddress(r.data.address || "");
        setDealerPhone(r.data.phone || "");
        setDealerEmail(r.data.email || "");
        setDealerDesc(r.data.description || "");
        setVatNumber(r.data.vat_number || "");
      });
    }
    api.get("/payment-methods").then((r) => setPaymentMethods(r.data || []));
  }, [user?.dealer_id]);

  const saveUser = async () => {
    setSavingUser(true);
    try {
      await api.patch("/users/me", {
        name,
        preferred_brands: preferredBrands,
        target_margin: Number(targetMargin) || 0,
        notifications_email: notifEmail,
        notifications_push: notifPush,
        language: i18n.language,
      });
      await reload();
      toast.success(t("profile.saved"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingUser(false);
    }
  };

  const saveDealer = async () => {
    if (!dealer) return;
    setSavingDealer(true);
    try {
      const { data } = await api.patch(`/dealers/${dealer.id}`, {
        name: dealerName,
        address: dealerAddress,
        phone: dealerPhone,
        email: dealerEmail,
        description: dealerDesc,
        vat_number: vatNumber,
      });
      setDealer(data);
      toast.success(t("profile.saved"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingDealer(false);
    }
  };

  const addPayment = async () => {
    try {
      const { data } = await api.post("/payment-methods", newPm);
      setPaymentMethods((p) => [...p, data]);
      setNewPm({ kind: "card", label: "", last4: "", iban_tail: "", holder: "" });
      setShowAddPayment(false);
      toast.success(t("profile.saved"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removePayment = async (id) => {
    try {
      await api.delete(`/payment-methods/${id}`);
      setPaymentMethods((p) => p.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleBrand = (b) => {
    setPreferredBrands((prev) =>
      prev.includes(b) ? prev.filter((x) => x !== b) : [...prev, b]
    );
  };

  return (
    <div className="py-6 md:py-10 space-y-8" data-testid="profile-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
            {t("profile.overline")}
          </p>
          <h1 className="mt-1 font-heading font-bold tracking-tighter text-3xl md:text-4xl lg:text-5xl text-slate-950">
            {t("profile.title")}
          </h1>
          <p className="mt-2 text-sm text-slate-500">{user?.email}</p>
        </div>
        {user?.dealer_id && (
          <Link
            to="/le-mie-auto"
            data-testid="profile-my-stock-cta"
            className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-slate-950 hover:bg-slate-800 text-white text-xs font-semibold px-4 py-2 transition-colors"
          >
            {t("nav.my_stock")} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>

      {/* Dealer company data */}
      {dealer && (
        <Section
          icon={Building2}
          title={t("profile.company_title")}
          data-testid="section-company"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label={t("profile.company_name")} testid="profile-dealer-name">
              <Input value={dealerName} onChange={(e) => setDealerName(e.target.value)} className="rounded-xl" />
            </Field>
            <Field label={t("profile.vat_number")} testid="profile-vat">
              <Input value={vatNumber} onChange={(e) => setVatNumber(e.target.value)} placeholder="IT01234567890" className="rounded-xl" />
            </Field>
            <Field label={t("profile.address")}>
              <Input value={dealerAddress} onChange={(e) => setDealerAddress(e.target.value)} className="rounded-xl" />
            </Field>
            <Field label={t("profile.phone")}>
              <Input value={dealerPhone} onChange={(e) => setDealerPhone(e.target.value)} className="rounded-xl" />
            </Field>
            <Field label={t("profile.email")}>
              <Input value={dealerEmail} onChange={(e) => setDealerEmail(e.target.value)} className="rounded-xl" />
            </Field>
            <Field label={t("profile.description")} span2>
              <Textarea value={dealerDesc} onChange={(e) => setDealerDesc(e.target.value)} rows={3} className="rounded-xl resize-none" />
            </Field>
          </div>
          <div className="mt-4 flex justify-end">
            <Button
              data-testid="save-dealer"
              onClick={saveDealer}
              disabled={savingDealer}
              className="rounded-full bg-blue-600 hover:bg-blue-700"
            >
              <Save className="w-4 h-4 mr-1" /> {t("profile.save")}
            </Button>
          </div>
        </Section>
      )}

      {/* Company documents (real GridFS-backed) */}
      {dealer && <CompanyDocumentsSection />}

      {/* Preferences */}
      <Section icon={Wallet} title={t("profile.preferences_title")} data-testid="section-preferences">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label={t("profile.your_name")}>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-xl" />
          </Field>
          <Field label={t("profile.target_margin")}>
            <Input
              type="number"
              value={targetMargin}
              onChange={(e) => setTargetMargin(e.target.value)}
              className="rounded-xl"
              data-testid="target-margin"
            />
          </Field>
          <div className="md:col-span-2">
            <Label className="text-xs font-semibold text-slate-600">
              {t("profile.preferred_brands")}
            </Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {BRAND_OPTIONS.map((b) => (
                <button
                  key={b}
                  onClick={() => toggleBrand(b)}
                  data-testid={`brand-${b}`}
                  className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors ${
                    preferredBrands.includes(b)
                      ? "bg-blue-600 border-blue-600 text-white"
                      : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {b}
                </button>
              ))}
            </div>
          </div>
          <div className="md:col-span-2 space-y-3 mt-2">
            <ToggleRow
              label={t("profile.notif_email")}
              value={notifEmail}
              onChange={setNotifEmail}
              icon={Bell}
              testid="notif-email"
            />
            <ToggleRow
              label={t("profile.notif_push")}
              value={notifPush}
              onChange={setNotifPush}
              icon={Bell}
              testid="notif-push"
            />
          </div>
          <div className="md:col-span-2 flex items-center gap-3 pt-2">
            <div className="text-xs text-slate-500 flex-1">
              <div className="font-semibold text-slate-700">{t("profile.language")}</div>
              <div>{t("profile.language_hint")}</div>
            </div>
            <LanguageSwitcher />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button
            data-testid="save-prefs"
            onClick={saveUser}
            disabled={savingUser}
            className="rounded-full bg-blue-600 hover:bg-blue-700"
          >
            <Save className="w-4 h-4 mr-1" /> {t("profile.save")}
          </Button>
        </div>
      </Section>

      {/* Payment methods */}
      <Section icon={CreditCard} title={t("profile.payments_title")} data-testid="section-payments">
        <div
          data-testid="profile-payment-notice"
          className="rounded-xl border border-blue-100 bg-blue-50/60 px-3 py-2 text-xs text-blue-900 mb-3"
        >
          {t("profile.payment_notice")}
        </div>
        <p className="text-xs text-slate-400 italic mb-3">{t("profile.payments_demo_note")}</p>
        {paymentMethods.length === 0 && !showAddPayment && (
          <p className="text-sm text-slate-500">{t("profile.no_payments")}</p>
        )}
        <ul className="space-y-2">
          {paymentMethods.map((pm) => (
            <li key={pm.id} className="flex items-center gap-3 rounded-xl border border-slate-100 p-3 text-sm">
              <div className="w-9 h-9 rounded-lg bg-slate-50 flex items-center justify-center text-slate-500">
                <CreditCard className="w-4 h-4" />
              </div>
              <div className="flex-1">
                <div className="font-heading font-semibold text-sm">{pm.label}</div>
                <div className="text-xs text-slate-500">
                  {pm.kind === "card" ? `**** **** **** ${pm.last4 || "0000"}` : `IBAN •••• ${pm.iban_tail || "0000"}`}
                </div>
              </div>
              <button
                data-testid={`delete-pm-${pm.id}`}
                onClick={() => removePayment(pm.id)}
                className="text-slate-400 hover:text-red-500 p-1"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
        {showAddPayment ? (
          <div className="mt-4 rounded-xl border border-slate-100 p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-600">{t("profile.pm_kind")}</Label>
                <Select value={newPm.kind} onValueChange={(v) => setNewPm({ ...newPm, kind: v })}>
                  <SelectTrigger className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="card">{t("profile.pm_card")}</SelectItem>
                    <SelectItem value="sepa">SEPA</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-600">{t("profile.pm_label")}</Label>
                <Input
                  value={newPm.label}
                  onChange={(e) => setNewPm({ ...newPm, label: e.target.value })}
                  placeholder={newPm.kind === "card" ? "Visa Business" : "Banca aziendale"}
                  className="rounded-xl mt-1"
                  data-testid="new-pm-label"
                />
              </div>
              {newPm.kind === "card" ? (
                <div>
                  <Label className="text-xs text-slate-600">{t("profile.pm_last4")}</Label>
                  <Input
                    value={newPm.last4}
                    onChange={(e) => setNewPm({ ...newPm, last4: e.target.value.slice(0, 4) })}
                    placeholder="1234"
                    className="rounded-xl mt-1"
                    data-testid="new-pm-last4"
                  />
                </div>
              ) : (
                <div>
                  <Label className="text-xs text-slate-600">{t("profile.pm_iban")}</Label>
                  <Input
                    value={newPm.iban_tail}
                    onChange={(e) => setNewPm({ ...newPm, iban_tail: e.target.value.slice(0, 4) })}
                    placeholder="6789"
                    className="rounded-xl mt-1"
                  />
                </div>
              )}
              <div>
                <Label className="text-xs text-slate-600">{t("profile.pm_holder")}</Label>
                <Input
                  value={newPm.holder}
                  onChange={(e) => setNewPm({ ...newPm, holder: e.target.value })}
                  className="rounded-xl mt-1"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAddPayment(false)} className="rounded-full">
                <X className="w-4 h-4 mr-1" />
                {t("common.cancel")}
              </Button>
              <Button
                data-testid="add-pm-save"
                onClick={addPayment}
                disabled={!newPm.label}
                className="rounded-full bg-blue-600 hover:bg-blue-700"
              >
                {t("profile.add_payment")}
              </Button>
            </div>
          </div>
        ) : (
          <Button
            data-testid="add-pm-button"
            variant="outline"
            className="mt-4 rounded-full"
            onClick={() => setShowAddPayment(true)}
          >
            + {t("profile.add_payment")}
          </Button>
        )}
      </Section>

      {/* Logout */}
      <div className="pt-2 flex items-center justify-between flex-wrap gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/strumenti"
            data-testid="profile-strumenti-link"
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white text-sm font-semibold text-slate-700 px-4 py-2 hover:border-blue-300 hover:text-blue-700 transition-colors"
          >
            <ShieldCheck className="w-4 h-4" />
            {t("profile.strumenti_link")}
          </Link>
          <Link
            to="/tariffario"
            data-testid="profile-tariff-link"
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white text-sm font-semibold text-slate-700 px-4 py-2 hover:border-blue-300 hover:text-blue-700 transition-colors"
          >
            <Receipt className="w-4 h-4" />
            {t("profile.tariff_link")}
          </Link>
        </div>
        <Button
          variant="outline"
          onClick={logout}
          data-testid="profile-logout"
          className="rounded-full"
        >
          <LogOut className="w-4 h-4 mr-1" />
          {t("auth.logout")}
        </Button>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children, ...rest }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="rounded-2xl bg-white border border-slate-100 p-6 shadow-sm"
      {...rest}
    >
      <div className="flex items-center gap-2 mb-4">
        {Icon && (
          <div className="w-8 h-8 rounded-lg bg-slate-50 text-slate-600 flex items-center justify-center">
            <Icon className="w-4 h-4" />
          </div>
        )}
        <h2 className="font-heading font-semibold text-lg tracking-tight text-slate-950">
          {title}
        </h2>
      </div>
      {children}
    </motion.div>
  );
}

function Field({ label, span2, children, testid }) {
  return (
    <div className={span2 ? "md:col-span-2" : ""} data-testid={testid}>
      <Label className="text-xs font-semibold text-slate-600">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function ToggleRow({ label, value, onChange, icon: Icon, testid }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-slate-100 p-3">
      <div className="inline-flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-slate-500" />}
        <span className="text-sm font-medium text-slate-800">{label}</span>
      </div>
      <Switch checked={value} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

function CompanyDocumentsSection() {
  const { t } = useTranslation();
  const [state, setState] = React.useState(null);
  const [busy, setBusy] = React.useState({});
  const inputRefs = React.useRef({});

  const reload = React.useCallback(() => {
    api
      .get("/dealers/me/documents")
      .then((r) => setState(r.data))
      .catch(() => setState({ items: [] }));
  }, []);
  React.useEffect(() => {
    reload();
  }, [reload]);

  const upload = async (slug, file) => {
    if (!file) return;
    setBusy((b) => ({ ...b, [slug]: true }));
    try {
      const fd = new FormData();
      fd.append("slug", slug);
      fd.append("name", file.name);
      fd.append("file", file);
      await api.post("/dealers/me/documents", fd);
      toast.success(t("profile.docs.uploaded"));
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy((b) => ({ ...b, [slug]: false }));
    }
  };

  const remove = async (slug) => {
    if (!window.confirm(t("common.cancel"))) {
      // Confirm using generic message; real confirmation handled after quick prompt.
    }
    setBusy((b) => ({ ...b, [slug]: true }));
    try {
      await api.delete(`/dealers/me/documents/${slug}`);
      toast.success(t("profile.docs.deleted"));
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy((b) => ({ ...b, [slug]: false }));
    }
  };

  const download = async (slug, name) => {
    try {
      const r = await api.get(`/dealers/me/documents/${slug}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = name || slug;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Section icon={FileText} title={t("profile.docs.title")}>
      <ul className="space-y-2" data-testid="profile-docs-list">
        {(state?.items || []).map((doc) => {
          const uploaded = !!doc.uploaded_at;
          return (
            <li
              key={doc.slug}
              data-testid={`profile-doc-${doc.slug}`}
              className="flex items-center gap-3 rounded-xl border border-slate-100 p-3 text-sm"
            >
              <div className="w-9 h-9 rounded-lg bg-slate-50 text-slate-600 flex items-center justify-center">
                <FileText className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-semibold text-sm truncate">
                  {t(`profile.docs.slug.${doc.slug}`)}
                  <span className="ml-2 text-[10px] uppercase tracking-wider font-semibold text-slate-400">
                    {doc.required ? t("profile.docs.required") : t("profile.docs.optional")}
                  </span>
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {uploaded
                    ? `${doc.name} · ${doc.size_kb} KB`
                    : t("profile.docs.empty").split(".")[0]}
                </div>
              </div>
              {uploaded && (
                <span
                  className={`text-xs font-semibold inline-flex items-center gap-1 ${
                    doc.verified ? "text-emerald-600" : "text-amber-600"
                  }`}
                >
                  <BadgeCheck className="w-3.5 h-3.5" />
                  {doc.verified ? t("profile.docs.verified") : t("profile.docs.pending")}
                </span>
              )}
              <div className="flex items-center gap-1">
                <input
                  type="file"
                  ref={(el) => (inputRefs.current[doc.slug] = el)}
                  className="hidden"
                  data-testid={`profile-doc-input-${doc.slug}`}
                  onChange={(e) => upload(doc.slug, e.target.files?.[0])}
                />
                <button
                  type="button"
                  disabled={busy[doc.slug]}
                  onClick={() => inputRefs.current[doc.slug]?.click()}
                  data-testid={`profile-doc-upload-${doc.slug}`}
                  className="text-[11px] font-semibold rounded-md px-2 py-1 bg-slate-50 hover:bg-slate-100 text-slate-700"
                >
                  {busy[doc.slug] ? t("profile.docs.uploading") : t("profile.docs.upload_cta")}
                </button>
                {uploaded && (
                  <>
                    <button
                      type="button"
                      onClick={() => download(doc.slug, doc.name)}
                      className="text-[11px] font-semibold rounded-md px-2 py-1 bg-slate-50 hover:bg-slate-100 text-slate-700"
                    >
                      {t("profile.docs.download_cta")}
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(doc.slug)}
                      className="text-[11px] font-semibold rounded-md px-2 py-1 text-red-600 hover:bg-red-50"
                    >
                      {t("profile.docs.delete_cta")}
                    </button>
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

